/* Orientation: what is due for review, the whole path, and the optional stages
   hanging off it. A row is a target, and a locked chapter says so without
   pretending to be clickable. */
import { useTranslation } from 'react-i18next';
import {
  core,
  dueWhen,
  dueList,
  extras,
  pad,
  progress as chapterProgress,
  srsOf,
  stateOf,
} from '../../engine/progress';
import { pRecall } from '../../engine/srs';
import { useApp } from '../../state/AppProvider';
import type { NodeDef } from '../../types';

export function Outline({ node }: { node: NodeDef }) {
  const { t } = useTranslation();
  const { progress, selectNode, startReview } = useApp();
  const due = dueList(progress);

  return (
    <>
      {due.length > 0 && (
        <div className="rail-group" data-outline="true" data-od-id="review-rail">
          <p className="eyebrow">
            {t('review')} · {due.length}
          </p>
          {due.map((row) => {
            const when = dueWhen(row.id, progress);
            const text = !when ? '' : when.kind === 'today' ? t('today') : `${when.days}${t('dayShort')}`;
            return (
              <button
                key={row.id}
                type="button"
                className="rail-row"
                data-act="review-one"
                data-id={row.id}
                data-due="true"
                onClick={() => startReview(row.id)}
              >
                <span className="rn num">{text}</span>
                <span className="rt">{t(row.titleKey)}</span>
                <span className="rp num">{Math.round(pRecall(srsOf(row.id, progress)) * 100)}%</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="rail-group" data-outline="true">
        <p className="eyebrow">{t('outline')}</p>
        {core().map((row) => (
          <RailRow key={row.id} node={row} current={node.id} onSelect={selectNode} />
        ))}
      </div>

      <div className="rail-group" data-outline="true">
        <p className="eyebrow">{t('outlineExtra')}</p>
        {extras().map((row) => (
          <RailRow key={row.id} node={row} current={node.id} onSelect={selectNode} />
        ))}
      </div>
    </>
  );
}

function RailRow({
  node,
  current,
  onSelect,
}: {
  node: NodeDef;
  current: string;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { progress } = useApp();
  const state = stateOf(node, progress);

  return (
    <button
      type="button"
      className="rail-row"
      data-act="node"
      data-id={node.id}
      aria-current={node.id === current}
      data-done={state === 'done'}
      aria-disabled={state === 'locked' || undefined}
      onClick={() => onSelect(node.id)}
    >
      <span className="rn num">{pad(node)}</span>
      <span className="rt">{t(node.titleKey)}</span>
      <span className="rp num">
        {chapterProgress(node, progress)}/{node.blocks.length}
      </span>
    </button>
  );
}
